import { EquityOrderAction, PriceType, PriceSelection } from '../../domain';
import { OrderEntryUtilityService } from './order-entry-utility.service';

export interface PriceObject {
  ask?: number;
  bid?: number;
  lastPrice?: number;
  limitPrice?: number;
}

/**
 * Service for making the calculations related to the order entry calculator components.
 */
export abstract class OrderEntryCalculatorService {
  /**
   * Checks if the order action is a buy action.
   * @param orderAction The action of the order (BUY, SELL, etc.)
   */
  static isBuy(orderAction: EquityOrderAction): boolean {
    return orderAction === EquityOrderAction.BUY || orderAction === EquityOrderAction.BUY_TO_COVER;
  }

  /**
   * Checks if the order action is a sell action.
   * @param orderAction The action of the order (BUY, SELL, etc.)
   */
  static isSell(orderAction: EquityOrderAction): boolean {
    return orderAction === EquityOrderAction.SELL || orderAction === EquityOrderAction.SHORT_SELL;
  }

  /**
   * Returns the price based on the order action and price type.
   * @param orderAction The action of the order (BUY, SELL, etc.)
   * @param orderPriceType The type of price for the order (MARKET, LIMIT, etc.)
   * @param priceObject An object containing the price details (ask, bid, last price, limit price)
   */
  static getPrice(orderAction: EquityOrderAction,
                  orderPriceType: PriceSelection,
                  priceObject: PriceObject): number {
    let price: number;
    if (orderPriceType === PriceType.LIMIT) {
      price = priceObject?.limitPrice;
    } else if (orderPriceType === PriceType.MARKET) {
      price = this.isBuy(orderAction) ? priceObject?.ask : priceObject?.bid;
      if (price === undefined || price === 0) {
        price = priceObject?.lastPrice;
      }
    }
    return price;
  }

  /**
   * This is currently only used by EasyTrade to determine what's the maximum order quantity
   * @param orderAction The action of the order (BUY or SELL, etc.)
   * @param realTimeCashBalance The real-time cash balance of the account
   * @param price The price of the security (in this case, should only use limit or ask due to checking for buy action)
   * @param maximumOrderQuantity The maximum order quantity for the order, received from the backend based on holdings
   */
  static calculateOrderQuantityMaximum(orderAction: EquityOrderAction,
                                       realTimeCashBalance: number,
                                       price: number,
                                       maximumOrderQuantity: number): number {
    if (this.isBuy(orderAction)) {
      return Math.floor(realTimeCashBalance / price);
    } else {
      return maximumOrderQuantity;
    }
  }

  /**
   * This is currently only used by EasyTrade to determine what's the maximum order amount
   * @param orderAction The action of the order (BUY or SELL, etc.)
   * @param maximumOrderQuantity The maximum order quantity for the order, received from the backend based on holdings
   * @param realTimeCashBalance The real-time cash balance of the account
   * @param bidPrice The bid price of the security, used for sell orders
   */
  static calculateOrderAmountMaximum(orderAction: EquityOrderAction,
                                     maximumOrderQuantity: number,
                                     realTimeCashBalance: number,
                                     bidPrice: number): number {
    return this.isSell(orderAction) && maximumOrderQuantity !== undefined
      ? parseFloat((maximumOrderQuantity * bidPrice).toFixed(2)) : realTimeCashBalance;
  }

  /**
   * Calculates the order amount based on the quantity and price.
   * @param quantity The order quantity
   * @param price The price of the security (limit, bid, ask, or last)
   */
  static calculateOrderAmount(quantity: number, price: number): number {
    if (price !== undefined && quantity !== undefined) {
      return price * quantity;
    }
    return undefined;
  }

  /**
   * Calculates the order quantity based on the amount, order action, and price.
   * @param amount The order amount
   * @param orderAction The action of the order (BUY or SELL, etc.)
   * @param price Get this price from getPrice
   * @param fractionalEligible Indicates if the order is eligible for fractional trading
   */
  static calculateOrderQuantity(amount: number,
                                orderAction: EquityOrderAction,
                                price: number,
                                fractionalEligible: boolean): number {
    if (price !== undefined && amount !== undefined) {
      if(fractionalEligible) {
        return OrderEntryUtilityService.truncateTill(amount / price, 5)
      } else {
        return this.isBuy(orderAction) ? Math.floor(amount / price) : Math.ceil(amount / price);
      }
    }
    return undefined;
  }
}
